<template>
  <div
    v-click-outside="closeList"
    class="select-classic"
    :class="{
      'has-active-option': hasActiveOption,
      locked: disabled,
    }"
  >
    <InputClassic
      v-if="searchBar"
      ref="selectClassicSearch"
      v-model="searchValue"
      :label="label"
      :placeholder="placeholder"
      :disabled="disabled"
      :inline="inline"
      :status="status"
      :helper="helper"
      :dropdown="inline"
      select-on-focus
      @focus="isOpen = true"
      @keyup.enter.native="submit()"
    >
      <template #label-icon>
        <!-- @slot Emplacement pour une icône à gauche du label de l'input. -->
        <slot name="label-icon" />
      </template>
      <template #input-icon>
        <IconDownTriangle class="down-triangle" />
      </template>
    </InputClassic>
    <button
      v-else
      class="toggle-list-btn"
      :class="{ open: isOpen, }"
      type="button"
      :disabled="disabled"
      @click="openList()"
      @keyup.esc="closeList()"
    >
      <span
        v-if="$slots['before-option']"
        class="text"
      >
        <!-- @slot Emplacement pour une icône à gauche de l'option. -->
        <slot name="before-option" />
      </span>
      <span
        v-if="selectedOption"
        class="select-classic-selected"
      >
        {{ selectedOption.label }}
      </span>
    </button>
    <template v-if="isOpen">
      <ul
        v-if="!optionsAreLoading"
        class="options-list-container text-medium"
        :class="{ top: optionsTop, }"
        @keyup.esc="closeList()"
      >
        <template v-if="optionsToDisplay && optionsToDisplay.length">
          <li
            v-for="(option, index) in optionsToDisplay"
            :key="`options-list-item-${index}`"
            class="options-list-item"
            :class="{ active: selectedOption.value === option.value, }"
          >
            <button
              class="options-list-item-btn flex-vcenter"
              type="button"
              @click="clickOption(option)"
            >
              {{ option.label }}
              <div class="flex-vcenter">
                <!--
                  @slot Emplacement pour un élément à droite de l'option.
                  @binding {Object} option Informations de l'élément du dropdown.
                -->
                <slot
                  name="after-option-item"
                  :option="option"
                />
                <UilCheck
                  v-if="selectedOption.value === option.value"
                  class="icon"
                  size="16"
                />
              </div>
            </button>
          </li>
        </template>
        <li
          v-else
          class="options-list-item"
        >
          {{ dropdownPlaceholder }}
        </li>
      </ul>
      <div
        v-else
        class="options-list-container"
        @keyup.esc="closeList()"
      >
        <InfiniteLoader />
      </div>
    </template>
  </div>
</template>

<script>
import InputClassic from "../../components/forms/InputClassic.vue";

import InfiniteLoader from "../../components/progress/InfiniteLoader.vue";

import IconDownTriangle from "../../components/icons/IconDownTriangle.vue";

import { UilCheck } from "@iconscout/vue-unicons";

/**
 * Affiche un dropdown pour sélectionner une option parmi une liste donnée.
 * Si besoin d'une implémentation accessible, voir le design pattern ARIA listbox.
 * @see https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html
 */
export default {
  name: "SelectClassic",
  components: {
    InputClassic,
    InfiniteLoader,
    IconDownTriangle,
    UilCheck,
  },
  model: {
    prop: "activeOption",
    event: "select",
  },
  props: {
    /**
     * Option sélectionnée.
     */
    activeOption: {
      type: Object,
      default: () => ({ id: null, label: "", value: "" }),
    },
    /**
     * Désactive le bouton.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Options à afficher dans le select
     * Ex: [{label: "OUI", value: "oui"}]
     */
    options: {
      type: Array,
      required: true,
    },
    /**
     * Indique si les résultats sont en cours de chargement.
     */
    optionsAreLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Ferme le select après avoir sélectionné un élément.
     */
    closeAfterSelect: {
      type: Boolean,
      default: true,
    },
    /**
     * Texte indicatif du champ.
     */
    placeholder: {
      type: String,
      default() { return this.$t("general.selectionner"); },
    },
    /**
     * Utilise la valeur par défaut si on n'a pas d'option sélectionnée.
     */
    usePlaceholder: {
      type: Boolean,
      default: true,
    },
    /**
     * Texte à utiliser si le dropdown n'a pas de résultats.
     */
    dropdownPlaceholder: {
      type: String,
      default() { return this.$t("recherche.aucun-resultat"); },
    },
    /**
     * Affiche un input pour rechercher un champ parmi les résultats.
     */
    searchBar: {
      type: Boolean,
      default: true,
    },
    /**
     * Effectue la recherche uniquement sur les options affichées.
     */
    internalSearch: {
      type: Boolean,
      default: true,
    },
    /**
     * Sélectionne la seul option quand il y en a une au enter.
     */
    selectOnEnter: {
      type: Boolean,
      default: false,
    },
    /**
     * Label du champ.
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Met le composant en ligne.
     */
    inline: {
      type: Boolean,
      default: false,
    },
    /**
     * Statut de la valeur dans l'input.
     */
    status: {
      type: String,
      validator: (value) => ["none", "valid", "warning", "error", "required", "locked"].includes(value),
      default: "none",
    },
    /**
     * Phrase d'aide sous le composant.
     */
    helper: {
      type: String,
      default: "",
    },
    /**
     * Délai avant ouverture (pour laisser charger les résultats).
     */
    debounceDelay: {
      type: Number,
      default: 750,
    },
    /**
     * Place le dropdown au-dessus.
     */
    optionsTop: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Déclenché lorsqu'une option est selectionnée.
     * @param {Object} option Infos relatives à l'option.
     */
    "select",
    /**
     * Déclenché lorsqu'on ferme le menu.
     */
    "close",
  ],
  data() {
    return {
      isOpen: false,
      inputValue: "",
      optionsFiltered: this.options,
      timeout: null,
    };
  },
  computed: {
    /**
     * Retourne l'option sélectionnée.
     * @returns {Object}
     */
    selectedOption: {
      get() {
        if (!this.usePlaceholder) {
          return this.hasActiveOption ? this.activeOption : this.options[0];
        }
        if (this.searchBar && !this.hasActiveOption) {
          return {};
        }
        return this.hasActiveOption ? this.activeOption : { label: this.placeholder, value: "" };
      },
      set(option) {
        this.$emit("select", option);

        if (this.closeAfterSelect) {
          this.closeList();
        }
      },
    },
    /**
     * Est-ce qu'une option a été choisie ?
     * @returns {Boolean}
     */
    hasActiveOption() {
      return this.activeOption?.value && this.activeOption.value !== "";
    },
    /**
     * Si la recherche est effectuée sur la liste affichée, alors on filtre les résultats.
     * Sinon, on emit la valeur de recherche.
     */
    searchValue: {
      get() {
        if (this.usePlaceholder && !this.hasActiveOption && !this.searchBar && this.placeholder) {
          return this.placeholder;
        }
        return this.inputValue;
      },
      set(val) {
        this.inputValue = val;

        if (this.internalSearch) {
          this.filterResults();
        } else {
          if (this.timeout) {
            clearTimeout(this.timeout);
          }
          this.timeout = setTimeout(
            () => this.$emit("search", this.inputValue), this.debounceDelay,
          );
        }
      },
    },
    /**
     * Si on a le champ de recherche dans le select, on affiche les options filtrées.
     * Sinon, on affiche les options du prop.
     */
    optionsToDisplay: {
      get() {
        return this.internalSearch ? this.optionsFiltered : this.options;
      },
      set(val) {
        this.optionsFiltered = val;
      },
    },
  },
  watch: {
    options() {
      this.optionsToDisplay = this.options;
    },
    selectedOption(newVal) {
      this.inputValue = newVal.label;
    },
  },
  mounted() {
    if (this.hasActiveOption && this.activeOption) {
      this.inputValue = this.activeOption.label;
    }
  },
  methods: {
    /**
     * Ouvre la liste des options et focus le champ d'input à l'ouverture.
     */
    openList() {
      this.isOpen = true;
      if (this.isOpen && this.searchBar) {
        this.$nextTick(() => this.$refs.selectClassicSearch.focus());
      }
    },
    /**
     * Ferme la liste des options.
     */
    closeList() {
      if (this.searchBar && this.hasActiveOption && this.inputValue !== this.selectedOption.label) {
        this.inputValue = this.selectedOption.label;
      }
      this.isOpen = false;
      this.$emit("close");
    },
    /**
     * Filtre les options du select selon le texte tapé.
     */
    filterResults() {
      if (!this.internalSearch) {
        throw new Error("Vous devez activer la recherche interne (internalSearch)");
      }
      this.optionsToDisplay = this.options.filter(
        (result) => result.label.toLowerCase().indexOf(this.inputValue.toLowerCase()) > -1,
      );
    },
    /**
     * Ajoute la sélection dans la valeur d'input ainsi que dans le select.
     * @param {Object} option Option sélectionnée dans le dropdown.
     */
    clickOption(option) {
      this.selectedOption = option;
    },
    /**
     * Si on n'a qu'une seule option, on la sélectionne et on submit.
     */
    submit() {
      if (this.selectOnEnter && this.optionsToDisplay && this.optionsToDisplay.length === 1) {
        this.clickOption(this.optionsToDisplay[0]);
      }
    },
  },
};
</script>

<style lang="scss">
@use "../../assets/styles/components/forms/select_classic.scss";
@use "../../assets/styles/components/forms/options_list.scss";
</style>
